import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Home,
    ArrowUpRight,
    Loader2,
    CheckCircle2,
} from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";
import { API, formatApiError } from "@/lib/adminApi";

const initial = {
    name: "", business_name: "", email: "", phone: "",
    business_address: "", current_website: "",
    preferred_contact: [],
    products_services: "", time_in_business: "", ideal_customer: "", unique_value: "",
    website_type: [], website_purpose: "", top_goals: "",
    look_and_feel: "", reference_sites: "", has_branding: "", colors_to_avoid: "",
    features: [],
    has_content: "", content_provision: "",
    pages_needed: [],
    owns_domain: "", has_hosting: "", needs_domain_help: "",
    service_areas: "", target_keywords: "", competitors: "",
    budget: "", completion_timeline: "", launch_date: "",
    ongoing_services: [],
    additional_info: "",
    website: "", // honeypot
};

const CONTACT_METHODS = ["Email", "Phone", "Text"];
const WEBSITE_TYPES = ["New Website", "Redesign", "Landing Page", "E-commerce", "Portfolio", "Blog", "Other"];
const FEATURES = [
    "Contact Form", "Quote Request", "Online Booking", "Online Store",
    "Payment Processing", "Blog", "Gallery", "FAQ", "Testimonials",
    "Newsletter Signup", "Live Chat", "Customer Accounts", "Other",
];
const PAGES = [
    "Home", "About", "Services", "Portfolio", "Pricing", "Contact",
    "FAQ", "Blog", "Privacy Policy", "Terms & Conditions", "Other",
];
const ONGOING = [
    "Website Maintenance", "Hosting Management", "SEO",
    "Performance Optimization", "Security Monitoring",
    "Content Updates", "Analytics Reporting",
];

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-[#666] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all duration-300 font-body";
const labelCls = "text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] mb-2 block font-medium";

function Field({ label, children, required }) {
    return (
        <div>
            <label className={labelCls}>
                {label}
                {required ? <span className="text-[#D4AF37] ml-1">*</span> : null}
            </label>
            {children}
        </div>
    );
}

function Section({ number, title, children }) {
    return (
        <section className="relative py-10 border-t border-white/[0.06]">
            <div className="flex items-baseline gap-4 mb-8">
                <span className="font-display text-4xl text-[#D4AF37]/40">
                    {String(number).padStart(2, "0")}
                </span>
                <h2 className="font-display text-2xl md:text-3xl text-white tracking-tight">
                    {title}
                </h2>
            </div>
            <div className="space-y-6">{children}</div>
        </section>
    );
}

function Chip({ selected, onClick, children, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-medium border transition-all duration-300 ${
                selected
                    ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                    : "bg-transparent text-[#C4C4C4] border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            }`}
        >
            {children}
        </button>
    );
}

export default function QuestionnairePage() {
    const navigate = useNavigate();
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
    const setVal = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const toggle = (k, v) =>
        setForm((p) => ({
            ...p,
            [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
        }));

    const validate = () => {
        const req = [
            ["name", "Full Name"], ["business_name", "Business Name"],
            ["email", "Email"], ["phone", "Phone Number"],
            ["products_services", "Products / Services"],
            ["time_in_business", "Time in Business"],
            ["ideal_customer", "Ideal Customer"], ["unique_value", "What Makes Your Business Unique"],
            ["website_purpose", "Website Purpose"], ["top_goals", "Top Goals"],
            ["look_and_feel", "Look & Feel"], ["reference_sites", "Reference Sites"],
            ["has_branding", "Existing Branding"],
            ["has_content", "Has Content"], ["content_provision", "Content Provision"],
            ["owns_domain", "Owns Domain"], ["has_hosting", "Has Hosting"],
            ["needs_domain_help", "Domain/Hosting Help"],
            ["service_areas", "Service Areas"], ["target_keywords", "Target Keywords"],
            ["competitors", "Competitors"],
            ["budget", "Budget"], ["completion_timeline", "Completion Timeline"],
        ];
        for (const [k, label] of req) {
            if (!String(form[k] || "").trim()) return `Please answer: ${label}`;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
        if (form.preferred_contact.length === 0) return "Please choose at least one preferred contact method.";
        if (form.website_type.length === 0) return "Please choose at least one website type.";
        if (form.features.length === 0) return "Please choose at least one website feature.";
        if (form.pages_needed.length === 0) return "Please choose at least one page.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            await axios.post(`${API}/questionnaire`, form);
            setSubmitted(true);
            toast.success("Questionnaire received. Thank you!");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="questionnaire-page" className="relative min-h-screen text-white font-body antialiased">
            <Navbar />

            <section className="relative pt-36 pb-8 luxe-radial overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
                    <button
                        data-testid="questionnaire-home-top"
                        onClick={() => navigate("/")}
                        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors duration-300 mb-12"
                    >
                        <Home className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Return Home
                    </button>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            Client Questionnaire
                        </span>
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-white font-light">
                        Tell us about your{" "}
                        <span className="italic text-gold-metallic">project</span>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base md:text-lg font-light leading-relaxed text-[#C4C4C4]">
                        A few thoughtful questions to help us design and build a
                        website that fits your business — beautifully.
                    </p>
                </div>
            </section>

            <section className="relative pb-24 luxe-radial-faint">
                <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16">
                    {submitted ? (
                        <div data-testid="questionnaire-success" className="py-24 text-center">
                            <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mx-auto mb-8" strokeWidth={1.25} />
                            <h2 className="font-display text-4xl text-white mb-4">
                                Received. Thank you.
                            </h2>
                            <p className="text-sm md:text-base text-[#C4C4C4] max-w-lg mx-auto leading-relaxed">
                                Your questionnaire is in. Our team will review your
                                responses and reach out shortly to schedule your
                                consultation and prepare your proposal.
                            </p>
                            <button
                                onClick={() => navigate("/")}
                                className="mt-10 inline-flex items-center gap-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] hover:border-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                            >
                                <Home className="w-4 h-4" strokeWidth={1.5} />
                                Return Home
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} data-testid="questionnaire-form" noValidate>
                            {/* Honeypot */}
                            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                                <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
                            </div>

                            <Section number={1} title="Contact Information">
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field label="Full Name" required>
                                        <input data-testid="q-name" type="text" className={inputCls} value={form.name} onChange={set("name")} />
                                    </Field>
                                    <Field label="Business Name" required>
                                        <input data-testid="q-business" type="text" className={inputCls} value={form.business_name} onChange={set("business_name")} />
                                    </Field>
                                    <Field label="Email Address" required>
                                        <input data-testid="q-email" type="email" className={inputCls} value={form.email} onChange={set("email")} />
                                    </Field>
                                    <Field label="Phone Number" required>
                                        <input data-testid="q-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} />
                                    </Field>
                                    <Field label="Business Address (optional)">
                                        <input type="text" className={inputCls} value={form.business_address} onChange={set("business_address")} />
                                    </Field>
                                    <Field label="Current Website (optional)">
                                        <input type="text" className={inputCls} value={form.current_website} onChange={set("current_website")} placeholder="https://" />
                                    </Field>
                                </div>
                                <Field label="Preferred Contact Method (select all that apply)" required>
                                    <div className="flex flex-wrap gap-2">
                                        {CONTACT_METHODS.map((m) => (
                                            <Chip key={m} testId={`contact-${m}`} selected={form.preferred_contact.includes(m)} onClick={() => toggle("preferred_contact", m)}>
                                                {m}
                                            </Chip>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section number={2} title="About Your Business">
                                <Field label="What products or services do you offer?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.products_services} onChange={set("products_services")} />
                                </Field>
                                <Field label="How long have you been in business?" required>
                                    <input type="text" className={inputCls} value={form.time_in_business} onChange={set("time_in_business")} />
                                </Field>
                                <Field label="Who is your ideal customer?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.ideal_customer} onChange={set("ideal_customer")} />
                                </Field>
                                <Field label="What makes your business unique?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.unique_value} onChange={set("unique_value")} />
                                </Field>
                            </Section>

                            <Section number={3} title="Project Overview">
                                <Field label="What type of website are you looking for? (select all that apply)" required>
                                    <div className="flex flex-wrap gap-2">
                                        {WEBSITE_TYPES.map((t) => (
                                            <Chip key={t} selected={form.website_type.includes(t)} onClick={() => toggle("website_type", t)}>{t}</Chip>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="What is the primary purpose of your website?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.website_purpose} onChange={set("website_purpose")} />
                                </Field>
                                <Field label="What are your top three goals for this project?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.top_goals} onChange={set("top_goals")} />
                                </Field>
                            </Section>

                            <Section number={4} title="Design Preferences">
                                <Field label="Describe the look and feel you want" required>
                                    <textarea className={`${inputCls} resize-none`} rows={4} value={form.look_and_feel} onChange={set("look_and_feel")} />
                                </Field>
                                <Field label="List 3–5 websites you like, and what you like about them" required>
                                    <textarea className={`${inputCls} resize-none`} rows={5} value={form.reference_sites} onChange={set("reference_sites")} />
                                </Field>
                                <Field label="Do you already have a logo and brand colors?" required>
                                    <div className="flex gap-2">
                                        {["Yes", "No"].map((v) => (
                                            <Chip key={v} selected={form.has_branding === v} onClick={() => setVal("has_branding", v)}>{v}</Chip>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Any colors or styles you'd like to avoid? (optional)">
                                    <textarea className={`${inputCls} resize-none`} rows={3} value={form.colors_to_avoid} onChange={set("colors_to_avoid")} />
                                </Field>
                            </Section>

                            <Section number={5} title="Website Features">
                                <Field label="Select the features you need (select all that apply)" required>
                                    <div className="flex flex-wrap gap-2">
                                        {FEATURES.map((f) => (
                                            <Chip key={f} selected={form.features.includes(f)} onClick={() => toggle("features", f)}>{f}</Chip>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section number={6} title="Content">
                                <Field label="Do you already have text, photos, videos, and branding assets?" required>
                                    <div className="flex gap-2">
                                        {["Yes", "No"].map((v) => (
                                            <Chip key={v} selected={form.has_content === v} onClick={() => setVal("has_content", v)}>{v}</Chip>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Will you provide the content, or would you like assistance writing it?" required>
                                    <div className="flex flex-wrap gap-2">
                                        {["I will provide the content", "I would like assistance writing it"].map((v) => (
                                            <Chip key={v} selected={form.content_provision === v} onClick={() => setVal("content_provision", v)}>{v}</Chip>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section number={7} title="Pages Needed">
                                <Field label="Which pages do you need? (select all that apply)" required>
                                    <div className="flex flex-wrap gap-2">
                                        {PAGES.map((p) => (
                                            <Chip key={p} selected={form.pages_needed.includes(p)} onClick={() => toggle("pages_needed", p)}>{p}</Chip>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section number={8} title="Domain & Hosting">
                                <div className="grid sm:grid-cols-3 gap-6">
                                    <Field label="Do you already own a domain?" required>
                                        <div className="flex gap-2">
                                            {["Yes", "No"].map((v) => <Chip key={v} selected={form.owns_domain === v} onClick={() => setVal("owns_domain", v)}>{v}</Chip>)}
                                        </div>
                                    </Field>
                                    <Field label="Do you have web hosting?" required>
                                        <div className="flex gap-2">
                                            {["Yes", "No"].map((v) => <Chip key={v} selected={form.has_hosting === v} onClick={() => setVal("has_hosting", v)}>{v}</Chip>)}
                                        </div>
                                    </Field>
                                    <Field label="Would you like help with domain / hosting?" required>
                                        <div className="flex gap-2">
                                            {["Yes", "No"].map((v) => <Chip key={v} selected={form.needs_domain_help === v} onClick={() => setVal("needs_domain_help", v)}>{v}</Chip>)}
                                        </div>
                                    </Field>
                                </div>
                            </Section>

                            <Section number={9} title="SEO">
                                <Field label="What cities or areas do you serve?" required>
                                    <input type="text" className={inputCls} value={form.service_areas} onChange={set("service_areas")} />
                                </Field>
                                <Field label="What keywords would you like to rank for?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={3} value={form.target_keywords} onChange={set("target_keywords")} />
                                </Field>
                                <Field label="Who are your main competitors?" required>
                                    <textarea className={`${inputCls} resize-none`} rows={3} value={form.competitors} onChange={set("competitors")} />
                                </Field>
                            </Section>

                            <Section number={10} title="Budget & Timeline">
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field label="Estimated budget" required>
                                        <input type="text" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="$" />
                                    </Field>
                                    <Field label="When would you like the project completed?" required>
                                        <input type="text" className={inputCls} value={form.completion_timeline} onChange={set("completion_timeline")} placeholder="e.g., within 6 weeks" />
                                    </Field>
                                    <Field label="Specific launch date? (optional)">
                                        <input type="date" className={inputCls} value={form.launch_date} onChange={set("launch_date")} />
                                    </Field>
                                </div>
                            </Section>

                            <Section number={11} title="Ongoing Services (Optional)">
                                <Field label="Which of the following are you interested in?">
                                    <div className="flex flex-wrap gap-2">
                                        {ONGOING.map((s) => (
                                            <Chip key={s} selected={form.ongoing_services.includes(s)} onClick={() => toggle("ongoing_services", s)}>{s}</Chip>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section number={12} title="Additional Information">
                                <Field label="Anything else you'd like us to know? (optional)">
                                    <textarea className={`${inputCls} resize-none`} rows={5} value={form.additional_info} onChange={set("additional_info")} />
                                </Field>
                            </Section>

                            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-white/[0.06]">
                                <p className="text-xs text-[#888] font-light">
                                    <span className="text-[#D4AF37]">*</span> indicates a required field
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="questionnaire-submit"
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black px-10 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158] disabled:opacity-60"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Submitting…
                                        </>
                                    ) : (
                                        <>
                                            Submit Questionnaire
                                            <ArrowUpRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>

            <Footer />
        </main>
    );
}
